import type { AxiosProgressEvent } from 'axios';
import { apiClient, unwrap } from './client';
import type {
  ApiEnvelope,
  ExportFormat,
  FileStatusPayload,
  GenerateResult,
  HistoryPayload,
  UploadPayload,
} from './types';

export interface UploadOptions {
  onUploadProgress?: (event: AxiosProgressEvent) => void;
  signal?: AbortSignal;
}

export async function uploadFile(
  userGUID: string,
  file: File,
  options: UploadOptions = {},
): Promise<UploadPayload> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await apiClient.post<ApiEnvelope<UploadPayload>>(
    `/api/user/${userGUID}/file/upload`,
    form,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: options.onUploadProgress,
      signal: options.signal,
    },
  );
  return unwrap(data);
}

// Максимум 10 страниц за один вызов (см. API.md — ограничение бэка).
export async function selectPages(
  userGUID: string,
  fileGUID: string,
  pages: number[],
): Promise<void> {
  await apiClient.post(`/api/user/${userGUID}/file/${fileGUID}/pages/select`, {
    pages,
  });
}

export async function getFileStatus(
  userGUID: string,
  fileGUID: string,
): Promise<FileStatusPayload> {
  const { data } = await apiClient.get<ApiEnvelope<FileStatusPayload>>(
    `/api/user/${userGUID}/file/${fileGUID}/status`,
  );
  return unwrap(data);
}

export interface HistoryQuery {
  page?: number;
  limit?: number;
}

export async function getHistory(
  userGUID: string,
  query: HistoryQuery = {},
): Promise<HistoryPayload> {
  const params: Record<string, number> = {
    page: query.page ?? 1,
    limit: query.limit ?? 20,
  };
  const { data } = await apiClient.get<ApiEnvelope<HistoryPayload>>(
    `/api/user/${userGUID}/history`,
    { params },
  );
  return unwrap(data);
}

// Превью PDF-страницы. Публичный эндпоинт без авторизации; принимает имя
// файла на диске, которое совпадает с guid/id из history.
// Работает same-origin через nginx-прокси (см. deploy/nginx/cp.onlyread.ru.conf).
export function pdfPageUrl(pdfGuid: string, index: number): string {
  const base = import.meta.env.VITE_API_BASE_URL || '';
  return `${base}/api/pdf/${pdfGuid}/page/${index}`;
}

// Превью страницы завершённого файла, альтернативный путь через привязанный к
// пользователю эндпоинт. Требует Authorization → через <img src> не пойдёт
// (без заголовка), используем fetch-ом где надо.
export function filePreviewUrl(
  userGUID: string,
  fileGUID: string,
  page: number,
): string {
  const base = import.meta.env.VITE_API_BASE_URL || '';
  return `${base}/api/user/${userGUID}/file/${fileGUID}?page=${page}`;
}

// Шаг 1: сервер генерирует файл (создаёт на диске), возвращает genFileName.
export async function generateExport(
  userGUID: string,
  fileGUID: string,
  format: ExportFormat,
  page: number = 0,
): Promise<GenerateResult> {
  const { data } = await apiClient.get<ApiEnvelope<GenerateResult>>(
    `/api/user/${userGUID}/file/${fileGUID}/generate/${format}`,
    { params: { page } },
  );
  return unwrap(data);
}

// Шаг 2: качаем сгенерированный файл. Важно: в URL идёт genFileName из generate(),
// а не исходный fileGUID.
export async function downloadExport(
  userGUID: string,
  genFileName: string,
  format: ExportFormat,
): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(
    `/api/user/${userGUID}/file/${genFileName}/download/${format}`,
    { responseType: 'blob' },
  );
  return data;
}
